import { providerPlannerKey } from "./providerSurfaceRegistry";
import { isYahooPlatform } from "./platformPresentationOrder";
const PLATFORM_LABELS = Object.freeze({
  meta: "Meta (Facebook + Instagram)",
  tiktok: "TikTok",
  youtube: "YouTube",
  ctv: "CTV",
  display: "Display",
  reserve_test: "Reserve/Test",
  google_search: "Google Search",
  chatgpt_search: "ChatGPT Search",
  x: "X",
  reddit: "Reddit",
  amazon: "Amazon",
  yahoo: "Yahoo!",
  viant: "Viant",
  shopify: "Shopify",
  linkedin: "LinkedIn",
  rumble: "Rumble",
  truth_social: "Truth Social",
  audio: "Audio",
  video: "Video",
  mobile: "Mobile",
  dooh: "DOOH",
  email: "Email",
  ingame: "In-Game",
});

const PLATFORM_ALLOCATION_ORDER = Object.freeze([
  "meta",
  "youtube",
  "ctv",
  "tiktok",
  "x",
  "google_search",
  "chatgpt_search",
  "rumble",
  "truth_social",
  "reddit",
  "amazon",
  "yahoo",
  "viant",
  "shopify",
  "linkedin",
  "video",
  "display",
  "mobile",
  "audio",
  "ingame",
  "dooh",
  "email",
  "reserve_test",
]);

const PLATFORM_ALLOCATION_ORDER_INDEX = new Map(
  PLATFORM_ALLOCATION_ORDER.map((key, index) => [key, index])
);

const comparePlatformOrder = (a, b, valueKey) => {
  const aIndex = PLATFORM_ALLOCATION_ORDER_INDEX.has(isYahooPlatform(a.key) ? "yahoo" : a.key)
    ? PLATFORM_ALLOCATION_ORDER_INDEX.get(isYahooPlatform(a.key) ? "yahoo" : a.key)
    : Number.MAX_SAFE_INTEGER;
  const bIndex = PLATFORM_ALLOCATION_ORDER_INDEX.has(isYahooPlatform(b.key) ? "yahoo" : b.key)
    ? PLATFORM_ALLOCATION_ORDER_INDEX.get(isYahooPlatform(b.key) ? "yahoo" : b.key)
    : Number.MAX_SAFE_INTEGER;
  if (aIndex !== bIndex) return aIndex - bIndex;
  if (aIndex === Number.MAX_SAFE_INTEGER && valueKey) {
    const delta = Number(b?.[valueKey] || 0) - Number(a?.[valueKey] || 0);
    if (delta !== 0) return delta;
  }
  return Number(a.firstIndex || 0) - Number(b.firstIndex || 0);
};

const normalizeText = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/\+/g, " and ")
    .replace(/[_-]+/g, " ")
    .replace(/[()]/g, " ")
    .replace(/\s+/g, " ");

const slugify = (value) =>
  normalizeText(value)
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

export const normalizePlatformAllocationKey = (value, options = {}) => {
  const providerKey = providerPlannerKey(value);
  if (providerKey) return providerKey;
  const allowUnknown = options.allowUnknown !== false;
  const text = normalizeText(value);
  if (!text) return "";

  if (/^viant(?: dsp)?(?: display)?$/.test(text.trim())) return "viant";

  if (/\b(meta|facebook|instagram|fb|ig)\b/.test(text)) return "meta";
  if (/\btik\s*tok\b|\btiktok\b/.test(text)) return "tiktok";
  if (/\byou\s*tube\b|\byoutube\b/.test(text)) return "youtube";
  if (/\bconnected\s*(tv|television)\b|\bctv\b|\bott\b/.test(text)) return "ctv";
  if (/\bmobile\b|\bin\s*app\b|\bin-app\b/.test(text)) return "mobile";
  if (/\bdisplay\b/.test(text)) return "display";
  if (/\breserve\b|\btest\b|\btesting\b|\bcontingency\b/.test(text)) return "reserve_test";
  if (/\bchat\s*gpt\b|\bchatgpt\b|\bopenai\b/.test(text)) return "chatgpt_search";
  if (/\bgoogle search\b|\bpaid search\b|\bsem\b/.test(text)) return "google_search";
  if (/^x$|\bx ads\b|\btwitter\b/.test(text)) return "x";
  if (/\breddit\b/.test(text)) return "reddit";
  if (/\bamazon\b/.test(text)) return "amazon";
  if (/\bshopify\b/.test(text)) return "shopify";
  if (/\blinkedin\b/.test(text)) return "linkedin";
  if (/\brumble\b/.test(text)) return "rumble";
  if (/\btruth social\b/.test(text)) return "truth_social";
  if (/\baudio\b/.test(text)) return "audio";
  if (/\bonline video\b|\bolv\b|\bvideo\b/.test(text)) return "video";
  if (/\bdooh\b|\bdigital out of home\b/.test(text)) return "dooh";
  if (/\bemail\b/.test(text)) return "email";
  if (/\bin game\b|\bingame\b/.test(text)) return "ingame";

  return allowUnknown ? slugify(value) : "";
};

// User-facing platform display-label normalization for Sherloc strategy
// output. Persisted production artifacts carry the historical
// displayName "Connected TV (CTV)" inside strategy_payload.strategyPresentation
// (immutable evidence — never mutated), while the backend emits "CTV" for
// future artifacts. Renderers therefore display-normalize by platform
// identity: any ctv-family platform (canonical key "ctv", or a legacy alias
// such as "connected-tv"/"Connected TV" resolving to it) renders exactly
// "CTV" regardless of the stored displayName. Every other platform's stored
// label passes through untouched. This is presentation-only: platform keys,
// alias parsing, allocation values, and hero-asset resolution are unaffected.
// Returns "" when no ctv identity resolves and displayName is empty, so
// callers keep their own fallback chains.
//
// Viant follows the same rule: the platform name is "Viant", and its reporting
// channel is not part of it. Historical plans may carry "Viant DSP (Display)"
// (or viant_dsp / "Viant DSP"); any identity resolving to viant renders exactly
// "Viant". Display only — it never makes Viant selectable in Cortex.
export const normalizeSherlocPlatformDisplayLabel = (platformKey, displayName) => {
  const label = String(displayName || "").trim();
  const resolvedKey =
    normalizePlatformAllocationKey(platformKey, { allowUnknown: false }) ||
    normalizePlatformAllocationKey(label, { allowUnknown: false });
  if (resolvedKey === "ctv") return "CTV";
  if (resolvedKey === "viant") return "Viant";
  return label;
};

const getChartPlatformIdentityCandidates = (valueOrRow) => {
  if (!valueOrRow || typeof valueOrRow !== "object" || Array.isArray(valueOrRow)) {
    return [valueOrRow];
  }
  return [
    valueOrRow.key,
    valueOrRow.platformKey,
    valueOrRow.platform_key,
    valueOrRow.platform,
    valueOrRow.platformLabel,
    valueOrRow.platform_label,
    valueOrRow.label,
    valueOrRow.name,
    valueOrRow.title,
    valueOrRow.platformName,
    valueOrRow.platform_name,
    valueOrRow.channel,
  ];
};

export const resolveCanonicalChartPlatformKey = (valueOrRow, options = {}) => {
  const candidates = getChartPlatformIdentityCandidates(valueOrRow).filter(
    (candidate) => candidate !== undefined && candidate !== null && String(candidate).trim()
  );
  for (const candidate of candidates) {
    const key = normalizePlatformAllocationKey(candidate, { allowUnknown: false });
    if (key) return key;
  }
  if (options.allowUnknown === false) return "";
  for (const candidate of candidates) {
    const key = normalizePlatformAllocationKey(candidate, { allowUnknown: true });
    if (key) return key;
  }
  return "";
};

const getPlatformAllocationLabel = (key, fallback = "") =>
  PLATFORM_LABELS[key] || String(fallback || "").trim() || "Platform";

const PLATFORM_FALLBACK_DETAILS = Object.freeze({
  meta: [
    "- Primary Role: Social audience response and retargeting.",
    "- Rationale: Meta-family placements support creative engagement, modeled audiences, and sequential retargeting across Facebook and Instagram.",
  ],
  tiktok: [
    "- Primary Role: Short-form awareness and engagement.",
    "- Rationale: TikTok helps spark discovery, creator-style response, and shareable moments for high-attention audiences.",
  ],
  youtube: [
    "- Primary Role: Trailer views and consideration.",
    "- Rationale: High-impact video placements support story comprehension, audience qualification, and release-window engagement.",
  ],
  ctv: [
    "- Primary Role: Reach and completed-view awareness.",
    "- Rationale: CTV delivers premium non-skippable viewing and broad household-level reinforcement for the campaign message.",
  ],
  display: [
    "- Primary Role: Conversion and retargeting support.",
    "- Rationale: Display keeps high-intent audiences warm with efficient reminders, sequential messaging, and lower-funnel capture.",
  ],
  reserve_test: [
    "- Primary Role: Optimization reserve.",
    "- Rationale: Reserve budget keeps the plan flexible for audience, creative, or platform signals that outperform during the flight.",
  ],
  google_search: [
    "- Primary Role: High-intent demand capture.",
    "- Rationale: Search converts active interest into measurable site traffic, ticketing intent, or landing-page response.",
  ],
  chatgpt_search: [
    "- Primary Role: AI search intent capture.",
    "- Rationale: ChatGPT Search reaches users expressing intent inside AI assistant search experiences; currently activated through StackAdapt as the buying path.",
  ],
  x: [
    "- Primary Role: Conversation and culture monitoring.",
    "- Rationale: X supports timely message testing, conversation response, and release-window signal amplification.",
  ],
  reddit: [
    "- Primary Role: Community interest validation.",
    "- Rationale: Reddit helps reach contextually aligned communities where discussion and niche intent can influence consideration.",
  ],
  amazon: [
    "- Primary Role: Commerce and shopper signal support.",
    "- Rationale: Amazon can connect audience interest with retail, commerce, or intent-driven media environments.",
  ],
  shopify: [
    "- Primary Role: Commerce intelligence and conversion planning layer.",
    "- Rationale: Shopify helps connect product, order, customer, cart, inventory, and revenue signals to media planning without implying paid media execution.",
  ],
  linkedin: [
    "- Primary Role: B2B and professional audience paid media.",
    "- Rationale: LinkedIn supports high-intent professional targeting, lead generation, thought leadership, and selective high-value audience reach.",
  ],
  rumble: [
    "- Primary Role: Incremental video reach.",
    "- Rationale: Rumble can add supplementary video scale among audiences that may be less concentrated on mainstream video platforms.",
  ],
  truth_social: [
    "- Primary Role: Incremental social reach.",
    "- Rationale: Truth Social can extend social distribution where the target audience or message context supports the placement.",
  ],
  audio: [
    "- Primary Role: Frequency and audio reinforcement.",
    "- Rationale: Audio keeps the message present across listening moments and supports efficient incremental reach.",
  ],
  video: [
    "- Primary Role: Online video attention.",
    "- Rationale: Online video supports scalable sight-sound-motion storytelling and measurable view-depth signals.",
  ],
  mobile: [
    "- Primary Role: Mobile-first reach and response.",
    "- Rationale: Mobile placements support location-aware delivery, app/web engagement, and fast response opportunities.",
  ],
  dooh: [
    "- Primary Role: Market-level awareness.",
    "- Rationale: DOOH reinforces campaign presence in priority geographies and high-traffic public environments.",
  ],
  email: [
    "- Primary Role: Direct response reinforcement.",
    "- Rationale: Email supports sequential messaging, reminder cadence, and audience reactivation when first-party reach is available.",
  ],
  ingame: [
    "- Primary Role: Immersive audience reach.",
    "- Rationale: In-game placements extend awareness into high-attention entertainment environments.",
  ],
});

const getPlatformFallbackDetails = (key) =>
  // Registry membership is identity, not evidence for invented provider strategy.
  // Preserve supplied provider notes; omit generic channel claims when absent.
  PLATFORM_FALLBACK_DETAILS[key] || (providerPlannerKey(key) ? [] : [
    "- Primary Role: Platform-specific support.",
    "- Rationale: This channel supports the plan through targeted delivery, audience response, and optimization signal capture.",
  ]);

const parsePercentValue = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const text = String(value ?? "").trim();
  if (!text) return null;
  const percentMatch = text.match(/(-?\d+(?:\.\d+)?)\s*%/);
  if (percentMatch) return Number(percentMatch[1]);
  const numeric = Number(text.replace(/,/g, ""));
  return Number.isFinite(numeric) ? numeric : null;
};

const parseAllocationPercentCandidate = (value) => {
  const parsed = parsePercentValue(value);
  if (parsed == null || !Number.isFinite(parsed)) return null;
  if (String(value ?? "").includes("%")) return parsed;
  return parsed >= 0 && parsed <= 100 ? parsed : null;
};

// Only fields that unambiguously carry a budget percentage are readable here.
// Generic weight/value/allocation/share-style fields are excluded on purpose:
// confidence scores, priority weights, and rank values must never be readable
// as budget percentages. The result is bounded to (0, 100] — anything outside
// that range is not a possible budget allocation and returns null.
const getAllocationPercent = (item) => {
  if (!item || typeof item !== "object") return null;
  const candidates = [
    item.percent,
    item.percentage,
    item.allocationPercent,
    item.allocation_percent,
    item.budgetPercent,
    item.budget_percent,
    item.budgetShare,
    item.sharePercent,
    item.share_percent,
  ];

  for (const candidate of candidates) {
    const parsed = parseAllocationPercentCandidate(candidate);
    if (parsed != null && Number.isFinite(parsed) && parsed > 0 && parsed <= 100) {
      return parsed;
    }
  }

  return null;
};

// Fields that unambiguously hold a dollar budget (never a percentage/share/weight).
const ALLOCATION_DOLLAR_BUDGET_FIELDS = [
  "grossAmount",
  "gross_amount",
  "grossBudget",
  "gross_budget",
  "grossAllocation",
  "gross_allocation",
  "budgetDollars",
  "budget_dollars",
  "allocatedBudget",
  "allocated_budget",
  "netWorkingMediaBudget",
  "net_working_media_budget",
  "workingMediaBudget",
  "working_media_budget",
  "netBudget",
  "net_budget",
  "netMediaBudget",
  "net_media_budget",
  "plannedSpend",
  "planned_spend",
  "spend",
  "flightBudget",
  "flight_budget",
];
// Generic fields that may legitimately hold dollars OR may carry a share/weight after an
// AI revision reshapes the payload. Only trusted as a last resort.
const ALLOCATION_AMBIGUOUS_BUDGET_FIELDS = [
  "budget",
  "amount",
  "total",
  "budgetLabel",
  "value",
];
const ALLOCATION_AMBIGUOUS_SHARE_FIELDS = [
  "allocation",
  "allocationValue",
  "allocation_value",
  "weight",
  "budget",
  "amount",
  "total",
  "value",
];

const parseAllocationDollarValue = (value) => {
  if (typeof value === "number") {
    return Number.isFinite(value) && value > 0 ? value : null;
  }
  const text = String(value ?? "").trim();
  if (!text) return null;
  // A value expressed as a percentage is a share, not a dollar budget.
  if (/%/.test(text)) return null;
  const multiplier = /m\b/i.test(text) ? 1000000 : /k\b/i.test(text) ? 1000 : 1;
  const cleaned = text.replace(/,/g, "").replace(/[^0-9.-]/g, "");
  if (!cleaned) return null;
  const parsed = Number.parseFloat(cleaned);
  return Number.isFinite(parsed) && parsed > 0 ? parsed * multiplier : null;
};

const getPlatformBudgetBasis = (context = {}) => {
  const candidates = [
    context.budgetBasis,
    context.netWorkingMediaBudget,
    context.net_working_media_budget,
    context.workingMediaBudget,
    context.working_media_budget,
    context.netMediaBudget,
    context.net_media_budget,
    context.grossBudget,
    context.gross_budget,
    context.totalBudget,
    context.total_budget,
  ];

  for (const candidate of candidates) {
    const parsed = parseAllocationDollarValue(candidate);
    if (parsed != null && Number.isFinite(parsed) && parsed > 0) return parsed;
  }

  return null;
};

const looksLikeCurrencyBudget = (value) => {
  if (typeof value === "number") return Math.abs(value) >= 1000;
  const text = String(value ?? "").trim();
  if (!text) return false;
  if (/[$,]/.test(text) || /\d\s*[km]\b/i.test(text)) return true;
  const digits = text.replace(/[^\d]/g, "");
  return digits.length >= 4;
};

const isShareLikeAmbiguousValue = (value, context = {}) => {
  const parsed = parseAllocationPercentCandidate(value);
  if (parsed == null || !Number.isFinite(parsed) || parsed <= 0) return false;
  if (String(value ?? "").includes("%")) return true;
  if (looksLikeCurrencyBudget(value)) return false;
  const basis = getPlatformBudgetBasis(context);
  return Number.isFinite(basis) && basis > 1000 && parsed <= 100;
};

const getAmbiguousSharePercent = (row = {}, context = {}) => {
  for (const field of ALLOCATION_AMBIGUOUS_SHARE_FIELDS) {
    if (!isShareLikeAmbiguousValue(row[field], context)) continue;
    return parseAllocationPercentCandidate(row[field]);
  }
  return null;
};

const parseAmbiguousBudgetValue = (value, context = {}) => {
  if (isShareLikeAmbiguousValue(value, context)) return null;
  return parseAllocationDollarValue(value);
};

export const resolvePlatformAllocationPercent = (allocation = {}, context = {}) => {
  if (context.percent != null && Number.isFinite(Number(context.percent))) {
    return Number(context.percent);
  }

  const row = allocation && typeof allocation === "object" ? allocation : {};
  const explicitPercent = getAllocationPercent(row);
  if (explicitPercent != null && Number.isFinite(explicitPercent)) return explicitPercent;

  return getAmbiguousSharePercent(row, context);
};

// Builds the campaign-wide platform sequence from final resolved allocation rows.
// Percentage descending is authoritative; source-row order is the stable tie-breaker.
export const buildPlatformAllocationRankMap = (allocationRows = [], context = {}) => {
  const rankedRows = (Array.isArray(allocationRows) ? allocationRows : [])
    .map((row, sourceIndex) => ({
      key: resolveCanonicalChartPlatformKey(row),
      percent: resolvePlatformAllocationPercent(row, context),
      sourceIndex,
    }))
    .filter(({ key, percent }) => key && Number.isFinite(percent) && percent > 0)
    .sort((a, b) => {
      const allocationDelta = b.percent - a.percent;
      return allocationDelta !== 0 ? allocationDelta : a.sourceIndex - b.sourceIndex;
    });

  const rankMap = new Map();
  rankedRows.forEach(({ key }) => {
    if (!rankMap.has(key)) rankMap.set(key, rankMap.size);
  });
  return rankMap;
};

export const orderPlatformRowsByAllocation = (
  rows = [],
  allocationRows = [],
  { context = {}, fallbackComparator = null } = {}
) => {
  const sourceRows = Array.isArray(rows) ? rows : [];
  const rankMap = buildPlatformAllocationRankMap(allocationRows, context);
  if (!rankMap.size) {
    return typeof fallbackComparator === "function"
      ? [...sourceRows].sort(fallbackComparator)
      : [...sourceRows];
  }

  return sourceRows
    .map((row, sourceIndex) => ({
      row,
      sourceIndex,
      rank: rankMap.get(resolveCanonicalChartPlatformKey(row)),
    }))
    .sort((a, b) => {
      const aRanked = Number.isInteger(a.rank);
      const bRanked = Number.isInteger(b.rank);
      if (aRanked && bRanked && a.rank !== b.rank) return a.rank - b.rank;
      if (aRanked !== bRanked) return aRanked ? -1 : 1;
      return a.sourceIndex - b.sourceIndex;
    })
    .map(({ row }) => row);
};

// Resolves a platform's dollar budget consistently for every microsite/chart surface.
// Priority: explicit dollar field → percentage × selected budget basis → generic field.
// This prevents an allocation percentage/share/weight (e.g. 35, 28, 9) from being rendered
// as a tiny dollar value (e.g. $35.00) after an AI revision reshapes the allocation rows.
export const resolvePlatformBudgetAmount = (allocation = {}, context = {}) => {
  const row = allocation && typeof allocation === "object" ? allocation : {};
  const totalBudget = getPlatformBudgetBasis(context);
  const explicitPercent = resolvePlatformAllocationPercent(row, context);

  for (const field of ALLOCATION_DOLLAR_BUDGET_FIELDS) {
    const dollars = parseAllocationDollarValue(row[field]);
    if (dollars != null) return dollars;
  }

  if (
    Number.isFinite(explicitPercent) &&
    explicitPercent > 0 &&
    Number.isFinite(totalBudget) &&
    totalBudget > 0
  ) {
    return (totalBudget * explicitPercent) / 100;
  }

  for (const field of ALLOCATION_AMBIGUOUS_BUDGET_FIELDS) {
    const dollars = parseAmbiguousBudgetValue(row[field], context);
    if (dollars != null) return dollars;
  }

  return null;
};

// --- Gross platform allocation display contract -----------------------------
// A platform recommendation surfaces its share of the campaign's TOTAL/GROSS
// budget (client-facing presentation value — never net media). All math runs in
// integer cents with percentages fixed to 4 decimal places so rounding is
// deterministic and free of floating-point display artifacts. When the approved
// percentages total exactly 100%, leftover cents are distributed by largest
// remainder (ties broken by row order) so the displayed allocations reconcile
// to the gross budget exactly; any other total keeps each platform's true
// independently-rounded share — the approved strategy is never silently
// normalized and no remainder is silently assigned to a final platform.

const GROSS_ALLOCATION_PERCENT_SCALE = 10000; // percent × 1e4 (4 decimal places)
const GROSS_ALLOCATION_FULL_BUDGET_UNITS = 100 * GROSS_ALLOCATION_PERCENT_SCALE;

export const toGrossBudgetCents = (grossBudget) => {
  const numeric = Number(grossBudget);
  if (!Number.isFinite(numeric) || numeric <= 0) return null;
  return Math.round(numeric * 100);
};

export const computeGrossAllocationCentsByPercent = (grossBudget, percents = []) => {
  const list = (Array.isArray(percents) ? percents : []).map((value) => Number(value));
  const grossBudgetCents = toGrossBudgetCents(grossBudget);
  if (grossBudgetCents == null) return list.map(() => null);

  const percentUnits = list.map((percent) =>
    Number.isFinite(percent) && percent > 0 && percent <= 100
      ? Math.round(percent * GROSS_ALLOCATION_PERCENT_SCALE)
      : null
  );
  const numerators = percentUnits.map((units) =>
    units == null ? null : grossBudgetCents * units
  );
  const percentUnitsTotal = percentUnits.reduce((sum, units) => sum + (units ?? 0), 0);
  const everyPercentValid = percentUnits.every((units) => units != null);

  if (!everyPercentValid || percentUnitsTotal !== GROSS_ALLOCATION_FULL_BUDGET_UNITS) {
    return numerators.map((numerator) =>
      numerator == null ? null : Math.round(numerator / GROSS_ALLOCATION_FULL_BUDGET_UNITS)
    );
  }

  const floors = numerators.map((numerator) =>
    Math.floor(numerator / GROSS_ALLOCATION_FULL_BUDGET_UNITS)
  );
  let leftoverCents = grossBudgetCents - floors.reduce((sum, cents) => sum + cents, 0);
  const byRemainder = numerators
    .map((numerator, index) => ({
      index,
      remainder: numerator - floors[index] * GROSS_ALLOCATION_FULL_BUDGET_UNITS,
    }))
    .sort((a, b) => b.remainder - a.remainder || a.index - b.index);
  const reconciled = floors.slice();
  for (const { index } of byRemainder) {
    if (leftoverCents <= 0) break;
    reconciled[index] += 1;
    leftoverCents -= 1;
  }
  return reconciled;
};

// en-US currency; whole-dollar allocations omit cents, materially-present cents
// are preserved. Returns "" (never "$0"/"NaN") for a missing amount.
export const formatGrossAllocationCentsAmount = (grossAllocationCents) => {
  if (!Number.isFinite(grossAllocationCents)) return "";
  const dollars = grossAllocationCents / 100;
  const hasCents = grossAllocationCents % 100 !== 0;
  return (
    "$" +
    dollars.toLocaleString(
      "en-US",
      hasCents
        ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
        : { maximumFractionDigits: 0 }
    )
  );
};

const roundOne = (value) => Math.round(Number(value || 0) * 10) / 10;

const formatPercent = (value) => {
  const rounded = roundOne(value);
  return Number.isInteger(rounded) ? `${rounded}%` : `${rounded.toFixed(1)}%`;
};

const formatCurrencyDigits = (digits) => {
  const numeric = Number(String(digits || "").replace(/[^\d]/g, ""));
  if (!Number.isFinite(numeric)) return digits;
  return numeric.toLocaleString("en-US");
};

const isAllocationCurrencyTerm = (value) =>
  /^[~≈]?\s*\$[\d,]+(?:\.\d{2})?$/.test(String(value || "").trim());

export const normalizeAllocationCurrencyText = (value) =>
  String(value || "")
    .replace(/\$(\d{1,3}(?:\/\d{3})+)(?=\D|$)/g, (match, amount) => {
      const digits = amount.replace(/\//g, "");
      return `$${formatCurrencyDigits(digits)}`;
    })
    .replace(/\$(\d{4,})(?=\D|$)/g, (match, amount) => `$${formatCurrencyDigits(amount)}`);

const normalizePercentValues = (values) => {
  const raw = values.map((value) => (Number.isFinite(Number(value)) ? Number(value) : 0));
  const sum = raw.reduce((total, value) => total + value, 0);
  if (sum <= 0) return raw;

  const scaled =
    Math.abs(sum - 100) > 0.05 ? raw.map((value) => (value / sum) * 100) : raw;
  const rounded = scaled.map(roundOne);
  const roundedSum = roundOne(rounded.reduce((total, value) => total + value, 0));
  const diff = roundOne(100 - roundedSum);

  if (Math.abs(diff) >= 0.1) {
    const maxIndex = rounded.reduce(
      (winner, value, index) => (value > rounded[winner] ? index : winner),
      0
    );
    rounded[maxIndex] = roundOne(rounded[maxIndex] + diff);
  }

  return rounded;
};

export const normalizePlatformAllocationItems = (items = []) => {
  if (!Array.isArray(items)) return [];

  const merged = new Map();

  items.forEach((item, index) => {
    if (!item || typeof item !== "object") return;
    const rawLabel =
      item.platform || item.channel || item.label || item.name || item.title || item.key || "";
    const key = normalizePlatformAllocationKey(rawLabel);
    const percent = getAllocationPercent(item);
    if (!key || percent == null || !Number.isFinite(percent)) return;

    const existing = merged.get(key);
    if (existing) {
      existing.value += percent;
      existing.sourceItems.push(item);
      return;
    }

    merged.set(key, {
      ...item,
      key,
      label: getPlatformAllocationLabel(key, rawLabel),
      value: percent,
      share: percent,
      percent,
      sourceItems: [item],
      firstIndex: index,
    });
  });

  const rows = Array.from(merged.values()).sort((a, b) =>
    comparePlatformOrder(a, b, "value")
  );
  return rows.map((row) => {
    const value = roundOne(row.value);
    return {
      ...row,
      value,
      share: value,
      percent: value,
      allocation: value,
      allocationLabel: formatPercent(value),
      shareLabel: formatPercent(value),
    };
  });
};

const stripBulletPrefix = (value) =>
  String(value || "")
    .trim()
    .replace(/^(?:[-*•]|[0-9]+[.)])\s+/, "")
    .trim();

const hasMeaningfulNotes = (notes = []) =>
  Array.isArray(notes) && notes.some((line) => stripBulletPrefix(line));

const cleanLabel = (value) =>
  stripBulletPrefix(value)
    .replace(/[:\-–—]\s*\d+(?:\.\d+)?\s*%.*$/i, "")
    .trim();

const parseInlineAllocationLine = (line) => {
  const stripped = stripBulletPrefix(line);
  const labelFirst = stripped.match(/^(.{1,90}?)(?:\s*[:\-–—]\s*|\s+)(\d+(?:\.\d+)?)\s*%(.*)$/i);
  if (labelFirst) {
    const key = normalizePlatformAllocationKey(labelFirst[1], { allowUnknown: false });
    if (key) {
      return {
        key,
        label: cleanLabel(labelFirst[1]),
        percent: Number(labelFirst[2]),
        phase: String(labelFirst[3] || "").trim(),
      };
    }
  }

  const percentFirst = stripped.match(/^(\d+(?:\.\d+)?)\s*%\s*(?:[:\-–—]?\s*)?(.{1,90})$/i);
  if (percentFirst) {
    const key = normalizePlatformAllocationKey(percentFirst[2], { allowUnknown: false });
    if (key) {
      return {
        key,
        label: cleanLabel(percentFirst[2]),
        percent: Number(percentFirst[1]),
        phase: "",
      };
    }
  }

  return null;
};

// A platform copy/notes line (a bullet, or a line that leads with a role/strategy label)
// is never a platform header — even when it mentions a platform name (e.g.
// "- Rationale: Meta-family inventory…"). Treating it as a header would split a block and
// strand its copy in the prologue.
const ALLOCATION_NOTE_PREFIX_PATTERN =
  /^(?:primary\s+role|secondary\s+role|role|rationale|funnel\s+stage|funnel|strategy|objective|audience|estimated\s+delivery|delivery|execution|measurement|optimization|format|creative|targeting|kpi|notes?)\b/i;

const isAllocationNoteLine = (line) => {
  const raw = String(line || "").trim();
  if (/^(?:[-*•]|[0-9]+[.)])\s+/.test(raw)) return true;
  return ALLOCATION_NOTE_PREFIX_PATTERN.test(stripBulletPrefix(line));
};

const parseHeaderLine = (line) => {
  if (isAllocationNoteLine(line)) return null;
  const stripped = stripBulletPrefix(line);
  if (!stripped || stripped.length > 90) return null;
  if (/^\d+(?:\.\d+)?\s*%/.test(stripped)) return null;
  const key = normalizePlatformAllocationKey(stripped, { allowUnknown: false });
  if (!key) return null;
  return { key, label: cleanLabel(stripped), percent: null, phase: "" };
};

const parsePercentLine = (line) => {
  const stripped = stripBulletPrefix(line);
  const match = stripped.match(/^(\d+(?:\.\d+)?)\s*%(.*)$/);
  if (!match) return null;
  return {
    percent: Number(match[1]),
    phase: String(match[2] || "").trim(),
  };
};

const extractPhaseTerms = (phase) => {
  const text = String(phase || "").trim();
  if (!text) return [];
  const inner = normalizeAllocationCurrencyText(text.match(/\(([^)]+)\)/)?.[1] || text)
    .replace(/^(?:[:\-–—]\s*)+/, "")
    .trim();
  if (isAllocationCurrencyTerm(inner)) return [inner];
  // Split on comma/slash separators, but never on a comma that is a currency thousands
  // separator (e.g. the comma in "$30,625"), so dollar amounts stay intact as one term.
  return inner
    .split(/\s*(?:\/+|,(?!\d{3}(?:\D|$)))\s*/)
    .map((entry) => entry.trim())
    .filter(Boolean);
};

const uniqueByNormalizedText = (items) => {
  const seen = new Set();
  return items.filter((item) => {
    const key = String(item || "").trim().toLowerCase().replace(/\s+/g, " ");
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const blockHasCurrencyPhase = (block) =>
  (Array.isArray(block?.phases) ? block.phases : []).some((phase) =>
    /\$\s*\d/.test(String(phase || ""))
  );

const mergeAllocationBlocks = (blocks, options = {}) => {
  const shouldNormalize = options.normalize !== false;
  const merged = new Map();

  blocks.forEach((block, index) => {
    if (!block?.key || !Number.isFinite(block.percent)) return;
    const existing = merged.get(block.key);
    if (existing) {
      // Duplicate representation of the SAME platform (e.g. an explicit "Meta: 35% — $30,625"
      // row plus a "Meta / 20% ($25,000)" grouped heading). Do NOT sum the percentages — that
      // produced corrupt totals like 55%. Prefer the row with the strongest explicit evidence
      // (an explicit dollar phase); otherwise keep the first occurrence. Only the notes are
      // merged so real platform copy from either representation survives.
      const preferIncoming =
        !blockHasCurrencyPhase(existing) && blockHasCurrencyPhase(block);
      if (preferIncoming) {
        existing.percent = block.percent;
        existing.phases = [...block.phases];
      }
      existing.notes.push(...block.notes);
      return;
    }

    merged.set(block.key, {
      ...block,
      notes: [...block.notes],
      phases: [...block.phases],
      firstIndex: index,
    });
  });

  const rows = Array.from(merged.values()).sort((a, b) =>
    comparePlatformOrder(a, b, "percent")
  );
  const normalizedValues = shouldNormalize
    ? normalizePercentValues(rows.map((row) => row.percent))
    : rows.map((row) => row.percent);

  return rows.map((row, index) => ({
    ...row,
    percent: normalizedValues[index],
    notes: uniqueByNormalizedText(row.notes),
    phases: uniqueByNormalizedText(row.phases),
  }));
};

const buildPhaseSuffix = (phases) => {
  const terms = uniqueByNormalizedText(phases.flatMap(extractPhaseTerms)).map(
    normalizeAllocationCurrencyText
  );
  return terms.length ? ` (${terms.join("/")})` : "";
};

const extractBudgetLabelFromPhases = (phases = []) =>
  uniqueByNormalizedText(phases.flatMap(extractPhaseTerms))
    .map(normalizeAllocationCurrencyText)
    .find((term) => /^\$[\d,]+(?:\.\d{2})?$/.test(term)) || "";

const isBudgetAllocationHeading = (line) =>
  /^\s*(?:A\.\s*)?Budget Allocation(?:\s+by\s+Platform)?\s*:?\s*$/i.test(
    String(line || "").trim()
  );

const isBudgetAllocationSectionTerminator = (line) => {
  const raw = String(line || "").trim();
  if (/^(?:[-*•]|[0-9]+[.)])\s+/.test(raw)) return false;
  const stripped = stripBulletPrefix(line);
  if (!stripped) return false;
  if (/^[B-Z]\.\s+/.test(stripped)) return true;
  // Supporting material inside the explicit allocation block legitimately
  // repeats platform + percent pairs (scale-down tiers, directional delivery
  // lines, net-media note) and must never be parsed as extra allocation rows.
  if (/^(?:scale-down tiers|net media allocation|directional projected)/i.test(stripped)) return true;
  return /^(?:channel strategy|channel roles?|audience(?:\s*\/\s*geo)?\s+strategy|flight(?:ing)?(?:\s*&\s*phasing)?|phasing|funnel(?:\s+stage)?|estimated\s+delivery|impression\s+logic|performance\s+forecast|optimization|measurement|creative|retargeting|implementation|next steps)\s*:?\s*$/i.test(
    stripped
  );
};

const extractBudgetAllocationBlocksFromText = (text) => {
  const source = normalizeAllocationCurrencyText(text);
  if (!source.trim()) return { source, lines: [], start: -1, end: -1, blocks: [], prologue: [] };

  const lines = source.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n");
  const headingIndexes = lines
    .map((line, index) => (isBudgetAllocationHeading(line) ? index : -1))
    .filter((index) => index >= 0);
  if (!headingIndexes.length) return { source, lines, start: -1, end: -1, blocks: [], prologue: [] };

  // A strategy can carry more than one Budget Allocation heading — e.g. a
  // narrative dollars-only overview followed by the explicit/repaired
  // "A. Budget Allocation by Platform" block that holds the real rows. Bind
  // deterministically to the first heading whose section yields allocation
  // rows instead of silently returning empty from a narrative-only section.
  let chosen = null;
  for (const start of headingIndexes) {
    const endCandidate = lines.findIndex(
      (line, index) => index > start && isBudgetAllocationSectionTerminator(line)
    );
    const end = endCandidate >= 0 ? endCandidate : lines.length;
    const sectionLines = lines.slice(start + 1, end);
    const { blocks, prologue } = parseAllocationBlocks(sectionLines);
    if (!chosen) chosen = { source, lines, start, end, blocks, prologue };
    if (blocks.length) return { source, lines, start, end, blocks, prologue };
  }
  return chosen;
};

// Returns the raw text of the chosen Budget Allocation section (heading line
// through the section's terminator), using the same multi-heading binding as
// extractBudgetAllocationBlocksFromText. Consumers use this to scope
// last-resort line scans so percent-bearing stats elsewhere in the strategy
// (KPI targets, benchmarks) can never be harvested as budget allocations.
// Returns "" when no Budget Allocation heading exists.
export const sliceBudgetAllocationSectionText = (text) => {
  const { lines, start, end } = extractBudgetAllocationBlocksFromText(text);
  if (start < 0) return "";
  return lines.slice(start, end).join("\n");
};

export const parseSherlocBudgetAllocationItems = (text) => {
  const { blocks } = extractBudgetAllocationBlocksFromText(text);
  if (!blocks.length) return [];

  return mergeAllocationBlocks(blocks, { normalize: false }).map((block) => {
    const label = getPlatformAllocationLabel(block.key, block.label);
    const budgetLabel = extractBudgetLabelFromPhases(block.phases);
    const role = uniqueByNormalizedText(block.notes)
      .find((line) => /\bprimary\s+role\b/i.test(line))
      ?.replace(/^(?:[-*•]\s*)?primary\s+role\s*:\s*/i, "")
      .trim() || "";
    return {
      key: block.key,
      label,
      platform: label,
      role,
      primaryObjective: role,
      percent: block.percent,
      share: block.percent,
      allocation: block.percent,
      value: block.percent,
      allocationLabel: formatPercent(block.percent),
      shareLabel: formatPercent(block.percent),
      budgetLabel,
      budget: budgetLabel || null,
      notes: block.notes,
      phases: block.phases,
      allocationSource: "strategy_text",
    };
  });
};

const parseAllocationBlocks = (sectionLines) => {
  const blocks = [];
  const prologue = [];
  let current = null;

  const finalize = () => {
    if (!current) return;
    if (current.key && Number.isFinite(current.percent)) {
      blocks.push(current);
    } else {
      prologue.push(...current.rawLines);
    }
    current = null;
  };

  sectionLines.forEach((line) => {
    if (current && current.percent == null) {
      const percent = parsePercentLine(line);
      if (percent) {
        current.percent = percent.percent;
        if (percent.phase) current.phases.push(percent.phase);
        current.rawLines.push(line);
        return;
      }
    }

    const inline = parseInlineAllocationLine(line);
    if (inline) {
      finalize();
      current = {
        key: inline.key,
        label: inline.label,
        percent: inline.percent,
        phases: inline.phase ? [inline.phase] : [],
        notes: [],
        rawLines: [line],
      };
      return;
    }

    const header = parseHeaderLine(line);
    if (header) {
      finalize();
      current = {
        key: header.key,
        label: header.label,
        percent: null,
        phases: [],
        notes: [],
        rawLines: [line],
      };
      return;
    }

    if (current) {
      current.notes.push(line);
      current.rawLines.push(line);
    } else {
      prologue.push(line);
    }
  });

  finalize();
  return { blocks, prologue };
};

export const normalizeSherlocBudgetAllocationText = (text) => {
  const { source, lines, start, end, blocks, prologue } =
    extractBudgetAllocationBlocksFromText(text);
  if (!source.trim() || start < 0 || !blocks.length) return source;

  const merged = mergeAllocationBlocks(blocks);
  const rebuilt = [
    lines[start],
    ...prologue.filter((line) => String(line || "").trim()),
    ...merged.flatMap((block) => {
      const label = getPlatformAllocationLabel(block.key, block.label);
      const notes = hasMeaningfulNotes(block.notes)
        ? block.notes.filter((line) => String(line || "").trim())
        : getPlatformFallbackDetails(block.key);
      return [
        label,
        `${formatPercent(block.percent)}${buildPhaseSuffix(block.phases)}`,
        ...notes,
      ];
    }),
  ];

  return [...lines.slice(0, start), ...rebuilt, ...lines.slice(end)].join("\n");
};
